import { CellRendererParams } from '../types'
import { ActivityCompaniesCellWithIcon } from '@/Components/TableCells/ActivityCompaniesCell'
import { ActivityDurationBadge } from '@/Components/TableCells/ActivityDurationBadge'
import { ActivityOverviewCell } from '@/Components/TableCells/ActivityOverviewCell'
import { ActivityPersonsCellWithIcon } from '@/Components/TableCells/ActivityPersonsCell'
import { ListItemsCountCell } from '@/Components/TableCells/ListItemsCountCell'
import { SingleContactReferenceCell } from '@/Components/TableCells/SingleContactReferenceCell'
import { DataClassName } from '@/Data/const'

/**
 * A single line rendered in the card body. `accessor` reads the attribute off
 * the data item (formatted via `getDisplayValue`) unless a `cell` renderer is
 * supplied — the same custom renderers used by the table columns can be reused
 * here. `main` marks the bold primary line (e.g. a person's name); `labelKey`
 * prefixes the value with a muted i18n label (e.g. "Updated <date>").
 */
export interface CardField {
  accessor: string
  /** i18n key for a muted prefix label shown before the value. */
  labelKey?: string
  /** i18n key for a label shown after the value (e.g. "1 253 Contacts"). */
  suffixKey?: string
  /** Bold primary line — typically the entity's name/title. */
  main?: boolean
  /** Reuse a table cell renderer instead of the default value formatting. */
  cell?: React.ComponentType<CellRendererParams>
  /** Render the value as a date using the short (`PP`) format. */
  date?: boolean
  /** Render the value as a date and time (weekday, date, time). */
  dateTime?: boolean
  /** Override the date-fns format string used when `date` or `dateTime` is true. */
  dateFormat?: string
  /**
   * The cell has no backing attribute value (e.g. an async items count) and
   * should always render. By default a `cell` field whose accessor resolves to
   * an empty value is skipped so it does not leave a blank row.
   */
  virtual?: boolean
}

export interface CardBadge {
  accessor: string
  /**
   * `enum` derives the badge colour from the attribute's enum group (status);
   * otherwise a fixed variant is used. Defaults to `info`.
   */
  variant?: 'enum' | 'info'
  /** Custom cell renderer for badges that require more than a plain value (e.g. time/duration). */
  cell?: React.ComponentType<CellRendererParams>
  /** The cell has no backing attribute value and should always render. */
  virtual?: boolean
}

export interface CardConfig {
  /** Visual variant matching the corresponding Figma card design. */
  variant: 'segment' | 'person'
  /**
   * Leading element in the card header. `entityIcon` renders the avatar/icon
   * for profile entities; `checkbox` renders the row-select checkbox. When
   * omitted, no leading element is shown.
   */
  leading?: 'checkbox' | 'entityIcon'
  /** Accessor shown as the bold title in the header row (next to `leading`). */
  titleAccessor?: string
  /** Cell renderer shown as the title in the header row, used when the title requires custom formatting (e.g. ActivityOverviewCell). Mutually exclusive with `titleAccessor`. */
  titleCell?: React.ComponentType<CellRendererParams>
  /**
   * Whether rows are multi-selectable. Controls the select-all header and the
   * per-row checkbox (only meaningful together with `leading: 'checkbox'`).
   */
  selectable?: boolean
  /** Optional badges row rendered under the header. */
  badges?: CardBadge[]
  /** Body fields, rendered top to bottom. */
  details: CardField[]
}

const SEGMENT_CARD_CONFIG: CardConfig = {
  variant: 'segment',
  leading: 'checkbox',
  titleAccessor: 'title',
  selectable: true,
  badges: [{ accessor: 'status', variant: 'enum' }, { accessor: 'type' }],
  details: [
    {
      accessor: 'itemsCount',
      cell: ListItemsCountCell,
      suffixKey: 'list.card.contacts',
      virtual: true,
    },
    { accessor: 'updatedAt', labelKey: 'list.card.updated', dateTime: true },
    {
      accessor: 'createdAt',
      labelKey: 'list.card.created',
      dateTime: true,
    },
    {
      accessor: 'createdBy',
      labelKey: 'list.card.by',
      cell: SingleContactReferenceCell,
    },
  ],
}

// The person card leads with the entity avatar and has no title/badges; the
// name is the bold primary line, followed by company, contact details and the
// change date (see the Persons overview design).
const PERSON_CARD_CONFIG: CardConfig = {
  variant: 'person',
  leading: 'entityIcon',
  details: [
    { accessor: 'name', main: true },
    { accessor: 'parent', cell: SingleContactReferenceCell },
    { accessor: 'email' },
    { accessor: 'phone' },
    { accessor: 'group' },
    {
      accessor: 'updatedAt',
      labelKey: 'overview.card.changeDate',
      dateTime: true,
    },
  ],
}

// The company card mirrors the person layout: entity avatar leading, name as
// the bold primary line, followed by group, region and the change date.
const COMPANY_CARD_CONFIG: CardConfig = {
  variant: 'person',
  leading: 'entityIcon',
  details: [
    { accessor: 'name', main: true },
    { accessor: 'group' },
    { accessor: 'region', cell: SingleContactReferenceCell },
    {
      accessor: 'updatedAt',
      labelKey: 'overview.card.changeDate',
      dateTime: true,
    },
  ],
}

// The activity card shows the type icon + subtype + title via ActivityOverviewCell,
// a tappable status badge, timeline, linked companies/persons, and updated date.
const ACTIVITY_CARD_CONFIG: CardConfig = {
  variant: 'segment',
  titleCell: ActivityOverviewCell,
  badges: [{ accessor: 'status', variant: 'enum' }, { accessor: 'priority' }],
  details: [
    {
      accessor: 'beginAt',
      labelKey: 'overview.card.beginAt',
      dateTime: true,
      dateFormat: 'eee, MMM d – h:mmaaa',
    },
    {
      accessor: 'endAt',
      labelKey: 'overview.card.endAt',
      dateTime: true,
      dateFormat: 'eee, MMM d – h:mmaaa',
    },
    { accessor: 'timeline', cell: ActivityDurationBadge, virtual: true },
    {
      accessor: 'companies',
      cell: ActivityCompaniesCellWithIcon,
      virtual: true,
    },
    { accessor: 'persons', cell: ActivityPersonsCellWithIcon, virtual: true },
    {
      accessor: 'updatedAt',
      labelKey: 'overview.card.changeDate',
      dateTime: true,
    },
  ],
}

const CARD_CONFIG: Partial<Record<string, CardConfig>> = {
  [DataClassName.ACTIVITY]: ACTIVITY_CARD_CONFIG,
  [DataClassName.LIST]: SEGMENT_CARD_CONFIG,
  [DataClassName.PERSON]: PERSON_CARD_CONFIG,
  [DataClassName.COMPANY]: COMPANY_CARD_CONFIG,
}

export const getCardConfig = (
  dataClassName?: string,
): CardConfig | undefined =>
  dataClassName ? CARD_CONFIG[dataClassName] : undefined
